// src/components/LocationEditorModal.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Modal, Button, Table, Form, Spinner,
} from 'react-bootstrap';
import AlertMessageUsageAccountModal from './AlertMessageUsageAccountModal';
import IncidentUserReadinessModal from './IncidentUserReadinessModal';
import axios from '../axiosAlertConfig';
import BuildingLocationsEditor from '../locations/BuildingLocationsEditor';
import { Power, Upload, Download, GeoAlt, ListTask, Megaphone, People } from 'react-bootstrap-icons';
import IncidentNotesModal from './IncidentNotesModal';
import Papa from 'papaparse';

 export default function LocationEditorModal({
  show,
  onHide,
  userContext,
  toggleActive,
  incident,
  onIncidentChange,
  onActionGuideVisibilityChange,
}) {
  const {
    iAccountID = '',
    iBuildingID = '',
    iCampusID = '-99',
    user,
  } = userContext;

  // The incident's building is authoritative while its page is open.
  const locationScope = {
    iAccountID: incident?.iAccountID || iAccountID,
    iBuildingID: incident?.iBuildingID || iBuildingID,
  };

  // ===== Action Guides =====
  const guideFileRef = useRef(null);
  const [guideRows, setGuideRows] = useState([]);
  const [guideBusy, setGuideBusy] = useState(false);
  const [guideEditMode, setGuideEditMode] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [showUsageModal, setShowUsageModal] = useState(false);

  const [showNotes, setShowNotes] = useState(false);
  const [showUserReadinessModal, setShowUserReadinessModal] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const [showActionGuideButtonSetting, setShowActionGuideButtonSetting] = useState(false);
const [actionGuideButtonBusy, setActionGuideButtonBusy] = useState(false);
const [actionGuideButtonSaving, setActionGuideButtonSaving] = useState(false);

const [geofenceDisabledSetting, setGeofenceDisabledSetting] = useState(false);
const [geofenceBusy, setGeofenceBusy] = useState(false);
const [geofenceSaving, setGeofenceSaving] = useState(false);
const [geofenceSource, setGeofenceSource] = useState('DEFAULT');

  // panel/menu state
  // menu -> shows just the button panel
  // locations -> shows location editor content
  // guides -> shows action guide content
  const [panel, setPanel] = useState('menu');

  const isActive = !!incident.isActive;


const [showPostMsgModal, setShowPostMsgModal] = useState(false);
const [postMsgDraft, setPostMsgDraft] = useState('');
const [postMsgSaving, setPostMsgSaving] = useState(false);

const openPostMsgModal = () => {
  setPostMsgDraft(String(incident?.adminPostMessage || ''));
  setShowPostMsgModal(true);
};

const savePostMsg = async (value) => {
  if (!incident?.incidentID) return;
  try {
    setPostMsgSaving(true);
    const { data } = await axios.put(`/ialert/incidents/${incident.incidentID}`, {
      adminPostMessage: String(value ?? ''),
    });
    if (onIncidentChange) onIncidentChange(data);
  } catch (e) {
    console.error('Save admin post message failed', e);
  } finally {
    setPostMsgSaving(false);
  }
};

const deletePostMsg = async () => {
  const ok = window.confirm('Delete (clear) the admin post message for this incident?');
  if (!ok) return;
  setPostMsgDraft('');
  await savePostMsg('');
};




  // Reset to button panel whenever the modal opens
  useEffect(() => {
    if (show) setPanel('menu');
  }, [show]);

    const loadActionGuideButtonVisibility = useCallback(async () => {
    if (!iAccountID) {
      setShowActionGuideButtonSetting(false);
      return;
    }

    try {
      setActionGuideButtonBusy(true);
      const { data } = await axios.get('/action-guides/action-guide-button-visibility', {
        params: { iAccountID },
      });
      setShowActionGuideButtonSetting(!!data?.showActionGuideButton);
    } catch (e) {
      console.error('Failed to load Action Guide button visibility', e);
      setShowActionGuideButtonSetting(false);
    } finally {
      setActionGuideButtonBusy(false);
    }
  }, [iAccountID]);

  useEffect(() => {
    if (show) loadActionGuideButtonVisibility();
  }, [show, loadActionGuideButtonVisibility]);

  const saveActionGuideButtonVisibility = async (checked) => {
    const prev = showActionGuideButtonSetting;
    const next = !!checked;

    setShowActionGuideButtonSetting(next);

    try {
      setActionGuideButtonSaving(true);
      const { data } = await axios.put('/action-guides/action-guide-button-visibility', {
        iAccountID,
        showActionGuideButton: next,
      });

      const savedValue = !!data?.showActionGuideButton;
      setShowActionGuideButtonSetting(savedValue);
      onActionGuideVisibilityChange?.(savedValue);
    } catch (e) {
      console.error('Failed to save Action Guide button visibility', e);
      setShowActionGuideButtonSetting(prev);
    } finally {
      setActionGuideButtonSaving(false);
    }
  };

    const geofenceScopeBuildingID = '-99';

  const loadGeofenceSetting = useCallback(async () => {
    if (!iAccountID) {
      setGeofenceDisabledSetting(false);
      setGeofenceSource('DEFAULT');
      return;
    }

    try {
      setGeofenceBusy(true);

      const { data } = await axios.get('/ialert/incidents/geofence-setting', {
        params: {
          iAccountID,
          iBuildingID: geofenceScopeBuildingID,
        },
      });

      setGeofenceDisabledSetting(!!data?.geofenceDisabled);
      setGeofenceSource(data?.setting?.source || 'DEFAULT');
    } catch (e) {
      console.error('Failed to load geofence setting', e);
      setGeofenceDisabledSetting(false);
      setGeofenceSource('DEFAULT');
    } finally {
      setGeofenceBusy(false);
    }
  }, [iAccountID]);

  useEffect(() => {
    if (show) loadGeofenceSetting();
  }, [show, loadGeofenceSetting]);

  const saveGeofenceDisabledSetting = async (checked) => {
    const prev = geofenceDisabledSetting;
    const next = !!checked;

    setGeofenceDisabledSetting(next);

    try {
      setGeofenceSaving(true);

      const updatedByName = [
        user?.firstName,
        user?.lastName,
      ].filter(Boolean).join(' ').trim();

      const { data } = await axios.put('/ialert/incidents/geofence-setting', {
        iAccountID,
        iBuildingID: geofenceScopeBuildingID,
        geofenceDisabled: next,
        updatedByUserID: user?.userID || user?.iUserID || null,
        updatedByName: updatedByName || user?.username || null,
      });

      setGeofenceDisabledSetting(!!data?.geofenceDisabled);
      setGeofenceSource(data?.setting?.source || 'DISTRICT');
    } catch (e) {
      console.error('Failed to save geofence setting', e);
      setGeofenceDisabledSetting(prev);
      alert(e?.response?.data?.message || 'Failed to save geofence setting.');
    } finally {
      setGeofenceSaving(false);
    }
  };

  // if parent modal closes, ensure confirm is not left open
  useEffect(() => {
    if (!show) setShowCloseConfirm(false);
  }, [show]);

  // ===== Action Guides =======================================================

 const loadGuides = useCallback(async () => {
  try {
    setGuideBusy(true);
    const { data } = await axios.get('/action-guides/pairs', {
      params: { iAccountID },
    });
    setGuideRows(Array.isArray(data) ? data : []);
  } catch (e) {
    console.error('Failed to load action guides', e);
    setGuideRows([]);
  } finally {
    setGuideBusy(false);
  }
}, [iAccountID]);

  useEffect(() => {
    if (show) loadGuides();
  }, [show, loadGuides]);

  const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const exportGuidesCsv = async () => {
    try {
      setGuideBusy(true);
      const { data } = await axios.get('/action-guides/pairs', {
        params: { iAccountID },
      });
      const rows = Array.isArray(data) ? data : [];

      const csv =
        'incidentType,category,guideText\n' +
        rows
          .map(r => ([
            csvEscape(r.incidentType),
            csvEscape(r.category),
            csvEscape(r.guideText || ''),
          ].join(',')))
          .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
       a.download = `action_guides_account_${iAccountID}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export action guides CSV failed', e);
    } finally {
      setGuideBusy(false);
    }
  };

  const cleanCell = (v) =>
    String(v ?? '')
      .replace(/^\uFEFF/, '') // BOM at start
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width
      .replace(/\r\n/g, '\n')
      .trimEnd();

  const pick = (obj, keys) => {
    for (const k of keys) {
      if (obj && obj[k] != null && String(obj[k]).trim() !== '') return obj[k];
    }
    return '';
  };

  const importGuidesFromCsvFile = (file) => {
    setGuideBusy(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      dynamicTyping: false,
      transformHeader: (h) => cleanCell(h).trim().toLowerCase(),
      complete: async (results) => {
        try {
          if (results?.errors?.length) {
            console.error('PapaParse errors:', results.errors.slice(0, 10));
          }

          const rows = Array.isArray(results.data) ? results.data : [];

          // support multiple header variants
          const guides = rows
            .map((r) => {
              const incidentType = cleanCell(
                pick(r, ['incidenttype', 'type']),
              ).trim();

              const category = cleanCell(
                pick(r, ['category']),
              ).trim();

              const guideText = cleanCell(
                pick(r, ['guidetext', 'actionguide', 'guide']),
              );

              return { incidentType, category, guideText };
            })
            .filter(r => r.incidentType && r.category);

          console.info('[ActionGuides] parsed CSV rows:', {
            totalRows: rows.length,
            guides: guides.length,
            sample: guides.slice(0, 5),
          });

          if (!guides.length) return;

          const replaceAll = window.confirm(
            'Replace ALL existing action guides for this account with this CSV?\n\nOK = Replace All\nCancel = Merge/Update only',
          );

          await axios.post('/action-guides/import', {
            iAccountID,
            mode: replaceAll ? 'replace' : 'merge',
            guides,
          });

          await loadGuides();
        } catch (e) {
          console.error('Action guides CSV import failed', e);
        } finally {
          setGuideBusy(false);
          if (guideFileRef.current) guideFileRef.current.value = '';
        }
      },
      error: (err) => {
        console.error('PapaParse failed:', err);
        setGuideBusy(false);
        if (guideFileRef.current) guideFileRef.current.value = '';
      },
    });
  };

  const handleGuideImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    importGuidesFromCsvFile(file);
  };

  const deleteAllGuides = async () => {
    const ok = window.confirm(
      'Delete ALL action guides?\n\nThis cannot be undone.',
    );
    if (!ok) return;

    try {
      setGuideBusy(true);
      await axios.delete('/action-guides', {
        data: { iAccountID},
      });
      await loadGuides();
    } catch (e) {
      console.error('Delete all action guides failed', e);
    } finally {
      setGuideBusy(false);
    }
  };

  const upsertGuide = async (incidentType, category, guideText) => {
    const key = `${incidentType}|||${category}`;
    try {
      setSavingKey(key);
      await axios.put('/action-guides/upsert', {
        iAccountID,
        incidentType,
        category,
        guideText: String(guideText || ''),
      });
    } catch (e) {
      console.error('Save action guide failed', e);
    } finally {
      setSavingKey('');
    }
  };

  // helper: determine target building IDs for alerts
  const getTargetBuildingIDs = () => {
    if (Array.isArray(incident.iBuildingIDs) && incident.iBuildingIDs.length) {
      return incident.iBuildingIDs;
    }
    if (incident.iBuildingID) {
      return [incident.iBuildingID];
    }
    if (iBuildingID) {
      return [iBuildingID];
    }
    return [];
  };

  // keep the exact same "incident closed" alert logic, just moved into a helper
  const sendIncidentClosedAlert = async () => {
    try {
      const modeLabel = String(incident.incidentMode || '').toUpperCase() || 'REAL';
      const typeLabel =
        incident.incidentTypeName ||
        incident.incidentType ||
        'Incident';

      const buildingIDs = getTargetBuildingIDs();

      await axios.post('/ialert/dispatch', {
        incidentID: incident.incidentID,
        iAccountID,
        iBuildingIDs: buildingIDs,
        requestedByUserID: user.userID,
        isSystemAdmin: !!user.isSystemAdmin,
        subject: `${modeLabel} Incident Closed – ${typeLabel}`,
        messageText: `The ${modeLabel} ${typeLabel} incident has been closed.`,
        voiceText: '', // no phone calls for close notice
        includeIncidentLink: false,
      });
    } catch (e) {
      console.error('Failed to send incident-closed alert', e);
    }
  };

  // NEW: button actions for the custom confirm modal
  const handleCloseSilently = () => {
    setShowCloseConfirm(false);
    toggleActive();
  };

  const handleCloseAndSendNotification = async () => {
    setShowCloseConfirm(false);
    await sendIncidentClosedAlert(); // even if this fails, we still close (same as before)
    toggleActive();
  };

  // close-incident handler now opens the custom confirm modal when closing an active incident
  const handleToggleActive = () => {
    if (isActive && incident && incident.incidentID) {
      setShowCloseConfirm(true);
      return;
    }
    toggleActive();
  };

  // False Alarm handler – sends false alarm message and closes incident
  const handleFalseAlarm = async () => {
    if (!isActive || !incident || !incident.incidentID) {
      return;
    }

    const confirmFalse = window.confirm(
      'Mark this incident as a FALSE ALARM and send a notification?',
    );
    if (!confirmFalse) return;

    try {
      const modeLabel = String(incident.incidentMode || '').toUpperCase() || 'REAL';
      const typeLabel =
        incident.incidentTypeName ||
        incident.incidentType ||
        'Incident';

      const buildingIDs = getTargetBuildingIDs();

      await axios.post('/ialert/dispatch', {
        incidentID: incident.incidentID,
        iAccountID,
        iBuildingIDs: buildingIDs,
        requestedByUserID: user.userID,
        isSystemAdmin: !!user.isSystemAdmin,
        subject: `${modeLabel} Incident FALSE ALARM – ${typeLabel}`,
        messageText: `The ${modeLabel} ${typeLabel} incident has been determined to be a FALSE ALARM. Normal operations may resume.`,
        voiceText: '', // no phone calls for false alarm notice
        includeIncidentLink: false,
      });
    } catch (e) {
      console.error('Failed to send false-alarm alert', e);
    }

    // close the incident after sending false-alarm message
    toggleActive();
  };

  const handleHide = () => {
    setShowCloseConfirm(false);
    onHide();
  };

  // Shared incident actions row (keeps existing capabilities)
  const IncidentActionsRow = () => (
    <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: '0.5rem' }}>
     
     
     
      <Button
        variant={isActive ? 'danger' : 'success'}
        size="sm"
        title={isActive ? 'Deactivate' : 'Activate'}
        onClick={handleToggleActive}
        className="d-inline-flex align-items-center gap-1 px-2"
      >
        <Power size={14} />
        {isActive ? 'Close Incident' : 'Open Incident'}
      </Button>

      {isActive && (
        <Button
          variant="warning"
          size="sm"
          onClick={handleFalseAlarm}
          className="d-inline-flex align-items-center gap-1 px-2"
        >
          False Alarm
        </Button>
      )}


<Button
  variant="outline-primary"
  size="sm"
  onClick={openPostMsgModal}
  className="d-inline-flex align-items-center gap-1 px-2"
>
  <Megaphone size={14} />
  Post Admin Message
</Button>

    </div>
  );

  const titleSuffix =
    panel === 'locations'
      ? ' — Location Editor'
      : panel === 'guides'
        ? ' — Action Guides'
        : '';

  return (
    <>
      <Modal show={show} onHide={handleHide} size="lg" centered>
        <Modal.Header closeButton>
          <div className="d-flex align-items-center w-100" style={{ gap: '0.5rem' }}>
            {panel !== 'menu' && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setPanel('menu')}
              >
                Back
              </Button>
            )}
            <Modal.Title className="mb-0">Manage Incident {titleSuffix}</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {/* =========================
              MENU: just a button panel
             ========================= */}
 {panel === 'menu' && (
  <> 
  <h3>Active Incident Tools</h3>
    <IncidentActionsRow />

<hr style={{marginBottom:'20px'}}/>
  <h3>After Action</h3>

    <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.5rem' }}>
      <Button
        variant="outline-dark"
        size="sm"
        onClick={() => setShowNotes(true)}
        title="Open Incident Notes"
      >
        Incident Notes
      </Button>

      <Button
        variant="outline-dark"
        size="sm"
        onClick={() => setShowUserReadinessModal(true)}
        title="Review user login, password, and incident participation activity"
        className="d-inline-flex align-items-center gap-1 px-2"
      >
        <People size={14} />
        User Login &amp; Incident Activity
      </Button>
    </div>

<hr style={{marginBottom:'20px'}}/>
    <h3>Admin Setup</h3>

    <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.5rem' }}>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => setPanel('locations')}
        className="d-inline-flex align-items-center gap-1 px-2"
      >
        <GeoAlt size={14} />
        Location Editor
      </Button>

      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => setPanel('guides')}
        className="d-inline-flex align-items-center gap-1 px-2"
      >
        <ListTask size={14} />
        Action Guides
      </Button>

      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => setShowUsageModal(true)}
        className="d-inline-flex align-items-center gap-1 px-2"
      >
        Message Usage
      </Button>
    </div>

    <div className="mt-3 p-2 border rounded">
      <Form.Check
        type="switch"
        id="show-action-guide-button-switch"
        label="Show Action Guide button on Active Incident page"
        checked={showActionGuideButtonSetting}
        disabled={actionGuideButtonBusy || actionGuideButtonSaving}
        onChange={(e) => void saveActionGuideButtonVisibility(e.target.checked)}
      />
      <div className="small text-muted mt-1">
        Account-level setting. Default is hidden.
        {actionGuideButtonBusy ? ' Loading…' : ''}
        {actionGuideButtonSaving ? ' Saving…' : ''}
      </div>
    </div>


 <div className="mt-3 p-2 border rounded">
  <Form.Check
    type="switch"
    id="disable-ialert-geofence-switch"
    label="Disable geofencing for incident creation"
    checked={geofenceDisabledSetting}
    // disabled={geofenceBusy || geofenceSaving}
    disabled={true}
    onChange={(e) => void saveGeofenceDisabledSetting(e.target.checked)}
  />

  <div className="small text-muted mt-1">
    Incident creation{' '}
    {geofenceDisabledSetting ? (
      <>
        <strong>WILL NOT</strong> require campus geofence validation.
      </>
    ) : (
      <>
        <strong>WILL</strong> require location permission and campus geofence validation.
      </>
    )}

    {geofenceSource ? ` Source: ${geofenceSource}.` : ''}
    {geofenceBusy ? ' Loading…' : ''}
    {geofenceSaving ? ' Saving…' : ''}
  </div>

  <div className="small text-muted mt-1">
    Applies district-wide.  
  </div>
</div>





  </>
)}
          {/* =========================
              LOCATION EDITOR VIEW
             ========================= */}
          {panel === 'locations' && (
            <BuildingLocationsEditor
              key={`${locationScope.iAccountID}:${locationScope.iBuildingID}`}
              scope={locationScope} client={axios}
            />
          )}

          {/* =========================
              ACTION GUIDES VIEW
             ========================= */}
          {panel === 'guides' && (
            <>
            

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <div className="fw-semibold">Account-Wide Action Guides</div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Form.Check
                    type="switch"
                    id="ag-edit-mode"
                    label="Edit mode"
                    checked={guideEditMode}
                    onChange={(e) => setGuideEditMode(e.target.checked)}
                  />

                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => void exportGuidesCsv()}
                  >
                    <Download size={14} className="me-1" />
                    Download Guide CSV
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => guideFileRef.current?.click()}
                  >
                    <Upload size={14} className="me-1" />
                    Upload Guide CSV
                  </Button>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => void deleteAllGuides()}
                  >
                    Delete All
                  </Button>

                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => void loadGuides()}
                    disabled={guideBusy}
                  >
                    Refresh
                  </Button>

                  <input
                    ref={guideFileRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleGuideImport}
                  />
                </div>
              </div>

              {guideBusy ? (
                <div className="d-flex justify-content-center my-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '22%' }}>Type</th>
                      <th style={{ width: '18%' }}>Category</th>
                      <th style={{ width: '60%' }}>Action Guide (outline bullets)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guideRows.map((r) => {
                      const key = `${r.incidentType}|||${r.category}`;
                      const isSaving = savingKey === key;

                      return (
                        <tr key={key}>
                          <td className="align-top">{r.incidentType}</td>
                          <td className="align-top">{r.category}</td>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={r.guideText || ''}
                              disabled={!guideEditMode}
                              placeholder="- First step\n  - Sub-step\n- Next step"
                              onChange={(e) => {
                                const v = e.target.value;
                                setGuideRows((prev) => (
                                  prev.map((x) => (
                                    x.incidentType === r.incidentType && x.category === r.category
                                      ? { ...x, guideText: v }
                                      : x
                                  ))
                                ));
                              }}
                              onBlur={() => {
                                const current = guideRows.find(
                                  x => x.incidentType === r.incidentType && x.category === r.category,
                                );
                                void upsertGuide(r.incidentType, r.category, current?.guideText || '');
                              }}
                            />
                            {isSaving ? (
                              <small className="text-muted">Saving…</small>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={handleHide}>Close</Button>
        </Modal.Footer>

        <IncidentNotesModal
          show={showNotes}
          onHide={() => setShowNotes(false)}
          incident={incident}
          user={user}
          onSaved={row => {
            if (onIncidentChange) onIncidentChange(row);
          }}
        />
      </Modal>

      {/* Close-confirm modal (unchanged behavior) */}
      <Modal
        show={showCloseConfirm}
        onHide={handleCloseSilently}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Close incident</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Send an alert message that this incident is now closed?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSilently}>
            Close Silently
          </Button>
          <Button variant="danger" onClick={() => void handleCloseAndSendNotification()}>
            Close And Send Notification
          </Button>
        </Modal.Footer>
      </Modal>




<Modal
  show={showPostMsgModal}
  onHide={() => setShowPostMsgModal(false)}
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Admin Post Message</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <Form.Label className="fw-semibold">
      Message shown in large text on the Active Incident page
    </Form.Label>

    <Form.Control
      as="textarea"
      rows={3}
      value={postMsgDraft}
      placeholder="Brief one-liner…"
      onChange={(e) => setPostMsgDraft(e.target.value)}
    />

    <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
      Characters: {String(postMsgDraft || '').length}
    </div>
  </Modal.Body>

  <Modal.Footer>
    <Button
      variant="outline-danger"
      onClick={() => void deletePostMsg()}
      disabled={postMsgSaving || !String(incident?.adminPostMessage || '').trim()}
    >
      Delete
    </Button>
    

    <Button
      variant="primary"
      onClick={() => void savePostMsg(postMsgDraft)}
      disabled={postMsgSaving}
    >
      {postMsgSaving ? 'Saving…' : 'Save'}
    </Button>

    <Button
      variant="secondary"
      onClick={() => setShowPostMsgModal(false)}
      disabled={postMsgSaving}
    >
      Close
    </Button>
  </Modal.Footer>
</Modal>


<AlertMessageUsageAccountModal
  show={showUsageModal}
  onHide={() => setShowUsageModal(false)}
  iAccountID={iAccountID}
  highlightIncidentID={incident?.incidentID}
  user={user}
/>

<IncidentUserReadinessModal
  show={showUserReadinessModal}
  onHide={() => setShowUserReadinessModal(false)}
  incident={incident}
/>







    </>
  );
}